import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { PencilIcon, FileTextIcon, ArrowRightIcon, CheckIcon, XIcon, UserIcon } from 'lucide-react';
export const ProductDescriptionsPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5328">
      <Navbar scrolled={scrolled} data-id="element-5329" />
      <main className="pt-24 pb-20" data-id="element-5330">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5331">
          <div className="max-w-4xl mx-auto" data-id="element-5332">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-5333">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-5334">
                <PencilIcon className="h-64 w-64" data-id="element-5335" />
              </div>
              <div className="relative z-10" data-id="element-5336">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5337">
                  Creating Compelling Product Descriptions
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-5338">
                  Learn how to write product descriptions that convert browsers
                  into buyers and effectively communicate the value of your
                  digital products.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-5339">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5340">
                Why Product Descriptions Matter
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-5341">
                Your product description is often the deciding factor in whether
                a potential customer makes a purchase. It's your chance to
                communicate value, overcome objections, and create desire for
                your digital product.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-5342">
                In a marketplace crowded with options, a well-crafted
                description helps your product stand out and connects with your
                ideal customer on both rational and emotional levels.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-5343">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-5344">
                  "The best product descriptions address specific customer pain
                  points and clearly articulate how your product solves them
                  better than any alternative."
                </p>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5345">
                The Anatomy of an Effective Product Description
              </h2>
              <div className="mb-8" data-id="element-5346">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5347">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5348">
                    1
                  </span>
                  Compelling Headline
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5349">
                  Your headline should instantly communicate what your product
                  is and its primary benefit. It's the first thing customers
                  see, so make it count.
                </p>
                <div className="ml-11 bg-gray-50 dark:bg-[#1A3330] p-5 rounded-lg mb-6" data-id="element-5350">
                  <div className="flex items-start mb-4" data-id="element-5351">
                    <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5352" />
                    <div data-id="element-5353">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5354">
                        Weak Example:
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5355">
                        "UI Kit for Websites"
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-5356">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5357" />
                    <div data-id="element-5358">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5359">
                        Strong Example:
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5360">
                        "Professional E-commerce UI Kit: Launch Your Online
                        Store in Half the Time"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-5361">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5362">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5363">
                    2
                  </span>
                  Benefits-Focused Opening
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5364">
                  Start with the most compelling benefits, not just features.
                  Explain how your product will improve the customer's life or
                  work.
                </p>
                <div className="ml-11 bg-gray-50 dark:bg-[#1A3330] p-5 rounded-lg mb-6" data-id="element-5365">
                  <div className="flex items-start mb-4" data-id="element-5366">
                    <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5367" />
                    <div data-id="element-5368">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5369">
                        Feature-focused (Weak):
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5370">
                        "This template includes 50+ components, 10 page layouts,
                        and responsive design."
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-5371">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5372" />
                    <div data-id="element-5373">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5374">
                        Benefit-focused (Strong):
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5375">
                        "Launch your professional website in hours instead of
                        weeks with our comprehensive template that makes you
                        look like you hired a top design agency – without the
                        agency price tag."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-5376">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5377">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5378">
                    3
                  </span>
                  Scannable Format
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5379">
                  Most customers scan rather than read every word. Use bullet
                  points, subheadings, and short paragraphs to make your
                  description easy to digest.
                </p>
                <div className="ml-11 bg-gray-50 dark:bg-[#1A3330] p-5 rounded-lg mb-6" data-id="element-5380">
                  <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-5381">
                    Effective Format Example:
                  </p>
                  <div className="space-y-4" data-id="element-5382">
                    <div data-id="element-5383">
                      <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5384">
                        ✨ Save Countless Design Hours
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-5385">
                        Get your product to market faster with our pre-designed
                        components.
                      </p>
                    </div>
                    <div data-id="element-5386">
                      <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5387">
                        🔍 Impress Your Clients
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-5388">
                        Present polished, professional designs that look
                        custom-made.
                      </p>
                    </div>
                    <div data-id="element-5389">
                      <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5390">
                        💰 Increase Your Profit Margins
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-5391">
                        Charge premium rates while reducing your production
                        time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5392">
                Advanced Techniques for Digital Product Descriptions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-5393">
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-5394">
                  <div className="flex items-center mb-4" data-id="element-5395">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3" data-id="element-5396">
                      <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-5397" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5398">
                      Customer-Centered Language
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5399">
                    Use "you" and "your" more than "we" and "our." Focus on the
                    customer's experience rather than your process.
                  </p>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-3 rounded-lg" data-id="element-5400">
                    <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-5401">
                      <span className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5402">
                        Instead of:
                      </span>{' '}
                      "We designed this template with attention to detail."
                    </p>
                    <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mt-2" data-id="element-5403">
                      <span className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5404">
                        Try:
                      </span>{' '}
                      "You'll impress clients with the thoughtful details in
                      every screen of your design."
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-5405">
                  <div className="flex items-center mb-4" data-id="element-5406">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3" data-id="element-5407">
                      <FileTextIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-5408" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5409">
                      Storytelling Elements
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5410">
                    Incorporate mini-stories about how your product can be used
                    in real-world scenarios.
                  </p>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-3 rounded-lg" data-id="element-5411">
                    <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-5412">
                      "Imagine presenting your client with a complete website
                      prototype in your very first meeting. Watch their eyes
                      light up as you scroll through perfectly designed pages
                      that look custom-built for their business..."
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-[#1A3330] pt-10 mt-10" data-id="element-5413">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5414">
                  Before and After Example
                </h2>
                <div className="bg-gray-50 dark:bg-[#1A3330] rounded-xl overflow-hidden mb-8" data-id="element-5415">
                  <div className="bg-red-50 dark:bg-red-900/20 p-6 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-5416">
                    <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-3 flex items-center" data-id="element-5417">
                      <XIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-5418" />
                      Before: Weak Product Description
                    </h3>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5419">
                      "Notion Template Pack includes 10 templates for
                      productivity and business. Works on all devices. Easy to
                      customize. Includes dashboard, task management, CRM,
                      notes, project management, and more. Save time with these
                      templates."
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-6" data-id="element-5420">
                    <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-3 flex items-center" data-id="element-5421">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-5422" />
                      After: Strong Product Description
                    </h3>
                    <div className="space-y-4" data-id="element-5423">
                      <p className="text-gray-600 dark:text-[#B0C1BD] font-medium text-lg" data-id="element-5424">
                        Transform Your Productivity: The Ultimate Notion
                        Workspace for Busy Professionals
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5425">
                        Stop wasting hours setting up your workspace. Get
                        instantly organized with our premium Notion template
                        pack designed specifically for freelancers and small
                        businesses.
                      </p>
                      <div data-id="element-5426">
                        <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5427">
                          🚀 Reclaim 5+ Hours Every Week
                        </p>
                        <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-5428">
                          Track projects, manage clients, and organize tasks in
                          one beautiful, connected system.
                        </p>
                      </div>
                      <div data-id="element-5429">
                        <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5430">
                          💼 Impress Clients with Professional Organization
                        </p>
                        <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-5431">
                          Never miss a deadline or lose an important document
                          again.
                        </p>
                      </div>
                      <div data-id="element-5432">
                        <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-5433">
                          🔄 Works Seamlessly Across All Your Devices
                        </p>
                        <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-5434">
                          Access your workspace from desktop, tablet, or mobile
                          with our responsive design.
                        </p>
                      </div>
                      <p className="text-gray-600 dark:text-[#B0C1BD] italic" data-id="element-5435">
                        "This template saved me from spreadsheet hell! My client
                        onboarding is now completely streamlined, and I look so
                        much more professional." — Sarah K., Freelance Designer
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#0D3C45]/5 dark:bg-[#0D3C45]/20 rounded-xl p-6 mt-12" data-id="element-5436">
                <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5437">
                  Key Takeaways
                </h2>
                <ul className="space-y-3" data-id="element-5438">
                  <li className="flex items-start" data-id="element-5439">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5440">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5441" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5442">
                      Focus on benefits over features—explain how your product
                      solves specific problems
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5443">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5444">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5445" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5446">
                      Make your description scannable with clear headings,
                      bullet points, and short paragraphs
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5447">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5448">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5449" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5450">
                      Use customer-centered language that speaks directly to
                      your ideal buyer
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5451">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5452">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5453" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5454">
                      Include social proof like testimonials to build trust and
                      credibility
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-5455">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5456">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5457">
                <a href="/learn/listing-checklist" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5458">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5459">
                    Product Listing Checklist
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5460">
                    A comprehensive checklist to ensure your product listings
                    are complete and optimized.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5461">
                    <span data-id="element-5462">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5463" />
                  </div>
                </a>
                <a href="/learn/product-screenshots" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5464">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5465">
                    Creating Perfect Product Screenshots
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5466">
                    Learn how to create professional screenshots that showcase
                    your digital products.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5467">
                    <span data-id="element-5468">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5469" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-5470">
              <h2 className="text-2xl font-bold mb-4" data-id="element-5471">
                Ready to write better product descriptions?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-5472">
                Apply these techniques to your digital products and start
                converting more browsers into buyers.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-5473">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5474" />
    </div>;
};